import React, { useCallback, useEffect, useState } from "react";
import { adminApi, formatApiError } from "../../../context/AuthContext";
import { Button } from "../../ui/button";
import { Input } from "../../ui/input";
import { Loader2, Search, UserPlus, BookUser } from "lucide-react";
import { toast } from "sonner";
import RecipientToCustomerAction from "./RecipientToCustomerAction";

/**
 * Herbruikbare ontvangers — adresboek van de verzendende klant.
 *
 * Sprint jun 2026: een ontvanger bestond alleen als snapshot op de zending,
 * dus dezelfde tante op Curaçao moest elke keer opnieuw worden ingetypt.
 * Deze picker leest het adresboek van de gekozen klant
 * (`GET /admin/customers/{id}/recipients`) en vult de ontvangervelden.
 */
export default function RecipientAddressBookPicker({
  customerId, onSelect, testIdPrefix = "recipient-book",
}) {
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState("");
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);

  const load = useCallback(async (term) => {
    if (!customerId) return;
    setLoading(true);
    try {
      const r = await adminApi.get(`/admin/customers/${customerId}/recipients`, {
        params: term ? { q: term } : {},
      });
      setRows(r.data?.recipients || []);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setLoading(false);
    }
  }, [customerId]);

  useEffect(() => {
    if (open) load(q);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  if (!customerId) return null;

  return (
    <div className="border border-dashed border-slate-300 bg-slate-50 p-2 rounded-md">
      <div className="flex flex-wrap items-center gap-2">
        <BookUser className="w-4 h-4 text-slate-500" />
        <span className="text-[11px] uppercase font-bold tracking-wide text-slate-600">
          Opgeslagen ontvangers van deze klant
        </span>
        <Button
          type="button" size="sm" variant="outline"
          onClick={() => setOpen((o) => !o)}
          className="ml-auto rounded-none h-7 text-[11px]"
          data-testid={`${testIdPrefix}-toggle`}
        >
          {open ? "Sluiten" : "Ontvanger zoeken"}
        </Button>
      </div>

      {open && (
        <div className="mt-2 space-y-2">
          <div className="flex gap-2">
            <div className="relative flex-1 min-w-0">
              <Search className="w-3.5 h-3.5 absolute left-2 top-1/2 -translate-y-1/2 text-slate-400" />
              <Input
                value={q}
                onChange={(e) => setQ(e.target.value)}
                onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); load(q); } }}
                placeholder="Naam, plaats, postcode, e-mail…"
                className="rounded-none pl-7 h-8 text-xs"
                data-testid={`${testIdPrefix}-search`}
              />
            </div>
            <Button
              type="button" size="sm" onClick={() => load(q)}
              className="rounded-none h-8 text-[11px] bg-[#082c50] text-white"
              data-testid={`${testIdPrefix}-search-btn`}
            >
              Zoeken
            </Button>
          </div>

          {loading ? (
            <div className="flex items-center gap-2 text-xs text-slate-500 py-2">
              <Loader2 className="w-3.5 h-3.5 animate-spin" /> Laden…
            </div>
          ) : rows.length === 0 ? (
            <div className="text-xs text-slate-500 italic py-1" data-testid={`${testIdPrefix}-empty`}>
              Nog geen opgeslagen ontvangers. Vink hieronder &quot;Ontvanger bewaren&quot; aan
              om deze ontvanger de volgende keer te kunnen kiezen.
            </div>
          ) : (
            <ul className="max-h-52 overflow-y-auto border border-slate-200 bg-white divide-y divide-slate-100">
              {rows.map((r) => (
                <li key={r.id}>
                  <button
                    type="button"
                    onClick={() => { onSelect(r); setOpen(false); }}
                    className="w-full text-left px-2 py-1.5 hover:bg-blue-50 transition-colors"
                    data-testid={`${testIdPrefix}-item-${r.id}`}
                  >
                    <div className="text-xs font-medium text-[#082c50] flex items-center gap-1.5">
                      <UserPlus className="w-3 h-3 text-slate-400" />
                      {r.name}
                      {r.company_name && <span className="text-slate-500">· {r.company_name}</span>}
                    </div>
                    <div className="text-[11px] text-slate-500 truncate">
                      {[r.street, r.house_number, r.postal_code, r.city, r.country]
                        .filter(Boolean).join(" ")}
                      {r.usage_count ? ` · ${r.usage_count}x gebruikt` : ""}
                    </div>
                  </button>
                  <div className="px-2 pb-1.5">
                    <RecipientToCustomerAction
                      recipient={r}
                      onConverted={() => load(q)}
                      testIdPrefix={`${testIdPrefix}-convert`}
                    />
                  </div>
                </li>
              ))}
            </ul>
          )}
        </div>
      )}
    </div>
  );
}

/** Adresboek-record → partij-velden van een zending. */
export const recipientBookToParty = (r) => ({
  name: r.name || "",
  company: r.company_name || "",
  phone: r.phone || "",
  whatsapp: r.whatsapp || "",
  email: r.email || "",
  street: r.street || "",
  house_number: r.house_number || "",
  house_number_addition: r.house_number_addition || "",
  postal_code: r.postal_code || "",
  city: r.city || "",
  country: r.country || "",
});
